import dayjs, { Dayjs } from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { uniqueId } from '../../../helpers/uniqueId'
import {
  FeatureFlag,
  FeatureFlagSaveInput,
  Segment,
  SegmentSaveInput,
  TRAFFIC_TYPE_TENANT,
} from '../../../domain/features/featureFlags'

dayjs.extend(relativeTime)

export const TAG_COLORS = [
  'blue',
  'green',
  'volcano',
  'gold',
  'purple',
  'magenta',
  'cyan',
  'red',
  'geekblue',
  'black',
  'default',
] as const

export const DEFAULT_TAG_COLOR = 'blue'

export type RuleDraft = {
  key: string
  treatment: string
  segments: string[]
  ids: number[]
}

export type TagDraft = {
  key: string
  label: string
  color: string | null
  timestamp: string | null
}

export type FlagDraft = {
  id: string
  treatments: string[]
  defaultTreatment: string
  rules: RuleDraft[]
  tags: TagDraft[]
}

export const createFlagDraft =(flag: FeatureFlag | null): FlagDraft => {
  if (!flag) {
    return {
      id: '',
      treatments: ['on', 'off'],
      defaultTreatment: 'off',
      rules: [],
      tags: [],
    }
  }
  return {
    id: flag.id,
    treatments: [...flag.treatments],
    defaultTreatment: flag.defaultTreatment,
    rules: flag.rules.map((rule) => ({
      key: uniqueId(),
      treatment: rule.treatment,
      segments: [...rule.segments],
      ids: [...rule.ids],
    })),
    tags: flag.tags.map((tag) => ({
      key: uniqueId(),
      label: tag.label,
      color: tag.color,
      timestamp: tag.timestamp,
    })),
  }
}

export const createTagDraft = (): TagDraft => {
  return {
    key: uniqueId(),
    label: '',
    color: DEFAULT_TAG_COLOR,
    timestamp: null,
  }
}

export const createRuleDraft = (treatments: string[], defaultTreatment: string): RuleDraft => {
  const nonDefaultTreatment = treatments.find(
    (treatment) => treatment.trim().toLowerCase() !== defaultTreatment.trim().toLowerCase()
  )
  return {
    key: uniqueId(),
    treatment: nonDefaultTreatment ?? defaultTreatment,
    segments: [],
    ids: [],
  }
}

export const renameTreatment = (draft: FlagDraft, index: number, newValue: string): FlagDraft => {
  const oldValue = draft.treatments[index]
  const treatments = draft.treatments.map((treatment, i) => (i === index ? newValue : treatment))
  const oldValueIsAmbiguous = draft.treatments.filter((treatment) => treatment === oldValue).length > 1
  if (oldValueIsAmbiguous) {
    return { ...draft, treatments }
  }
  return {
    ...draft,
    treatments,
    defaultTreatment: draft.defaultTreatment === oldValue ? newValue : draft.defaultTreatment,
    rules: draft.rules.map((rule) =>
      rule.treatment === oldValue ? { ...rule, treatment: newValue } : rule
    ),
  }
}

export const removeTreatment = (draft: FlagDraft, index: number): FlagDraft => {
  return {
    ...draft,
    treatments: draft.treatments.filter((_, i) => i !== index),
  }
}

export const moveRule = (rules: RuleDraft[], index: number, direction: 'up' | 'down'): RuleDraft[] => {
  const newIndex = direction === 'up' ? index - 1 : index + 1
  if (newIndex < 0 || newIndex >= rules.length) {
    return rules
  }
  const reordered = [...rules]
  const [moved] = reordered.splice(index, 1)
  reordered.splice(newIndex, 0, moved)
  return reordered
}

type FlagDraftValidationOptions = {
  isNew: boolean
  existingFlagIds: string[]
}

export const validateFlagDraft = (draft: FlagDraft, options: FlagDraftValidationOptions): string[] => {
  const errors: string[] = []
  const flagId = draft.id.trim()
  if (options.isNew) {
    if (flagId.length === 0) {
      errors.push('Flag id is required.')
    } else if (options.existingFlagIds.includes(flagId)) {
      errors.push(`A feature flag with id '${flagId}' already exists.`)
    }
  }

  const treatments = draft.treatments.map((treatment) => treatment.trim())
  if (treatments.length === 0) {
    errors.push('At least one treatment is required.')
  }
  if (treatments.some((treatment) => treatment.length === 0)) {
    errors.push('Treatments cannot be blank.')
  }
  if (treatments.some((treatment) => treatment.includes(','))) {
    errors.push('Treatments cannot contain commas.')
  }
  const lowerTreatments = treatments.map((treatment) => treatment.toLowerCase())
  if (new Set(lowerTreatments).size !== lowerTreatments.length) {
    errors.push('Treatments must be unique.')
  }

  if (treatments.length > 0 && !lowerTreatments.includes(draft.defaultTreatment.trim().toLowerCase())) {
    errors.push('The default treatment must be one of the treatments.')
  }

  draft.rules.forEach((rule, index) => {
    if (!lowerTreatments.includes(rule.treatment.trim().toLowerCase())) {
      errors.push(`Rule ${index + 1}: the treatment must be one of the flag's treatments.`)
    }
  })

  const tagLabels = draft.tags.map((tag) => tag.label.trim())
  if (tagLabels.some((label) => label.length === 0)) {
    errors.push('Tag labels cannot be blank.')
  }
  const lowerTagLabels = tagLabels.map((label) => label.toLowerCase())
  if (new Set(lowerTagLabels).size !== lowerTagLabels.length) {
    errors.push('Tag labels must be unique.')
  }

  return errors
}

export const toFlagSaveInput = (draft: FlagDraft): FeatureFlagSaveInput => {
  return {
    treatments: draft.treatments.map((treatment) => treatment.trim()),
    defaultTreatment: draft.defaultTreatment.trim(),
    trafficType: TRAFFIC_TYPE_TENANT,
    rules: draft.rules.map((rule) => ({
      treatment: rule.treatment.trim(),
      segments: rule.segments,
      ids: [...rule.ids],
    })),
    tags: draft.tags
      .filter((tag) => tag.label.trim().length > 0)
      .map((tag) => ({
        label: tag.label.trim(),
        color: tag.color,
      })),
  }
}

export type SegmentDraft = {
  name: string
  tenantIds: number[]
}

export const createSegmentDraft = (segment: Segment | null): SegmentDraft => {
  if (!segment) {
    return { name: '', tenantIds: [] }
  }
  return { name: segment.name, tenantIds: [...segment.tenantIds] }
}

export const validateSegmentDraft = (draft: SegmentDraft): string[] => {
  const errors: string[] = []
  if (draft.name.trim().length === 0) {
    errors.push('Segment name is required.')
  }
  return errors
}

export const toSegmentSaveInput = (draft: SegmentDraft): SegmentSaveInput => {
  return {
    name: draft.name.trim(),
    tenantIds: [...draft.tenantIds],
  }
}

export const flagIdsUsingSegment = (segmentId: string, features: FeatureFlag[]): string[] => {
  return features
    .filter((flag) => flag.rules.some((rule) => rule.segments.includes(segmentId)))
    .map((flag) => flag.id)
}

export const filterFeatureFlags = (features: FeatureFlag[], search: string): FeatureFlag[] => {
  const term = search.trim().toLowerCase()
  if (term.length === 0) {
    return features
  }
  return features.filter(
    (flag) =>
      flag.id.toLowerCase().includes(term) ||
      flag.tags.some((tag) => tag.label.toLowerCase().includes(term))
  )
}

export const LAST_USAGE_TIMESTAMP_FORMAT = 'MMM D, YYYY h:mm A'

export const formatLastUsedAt = (lastUsedAt: string | null): string =>
  lastUsedAt ? dayjs(lastUsedAt).format(LAST_USAGE_TIMESTAMP_FORMAT) : 'Never'

export const formatLastUsedRelative = (lastUsedAt: string | null): string =>
  lastUsedAt ? dayjs(lastUsedAt).fromNow() : 'Never'

export const RECENT_TRAFFIC_DAYS = 7

export const hasRecentTraffic = (lastUsedAt: string | null, now: Dayjs = dayjs()): boolean => {
  if (!lastUsedAt) {
    return false
  }
  return dayjs(lastUsedAt).isAfter(now.subtract(RECENT_TRAFFIC_DAYS, 'day'))
}

export const getTreatmentTagColor = (treatment: string): string => {
  switch (treatment.trim().toLowerCase()) {
    case 'off':
      return 'red'
    case 'on':
      return 'green'
    default:
      return 'blue'
  }
}
